import { TestTube, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  DialogFooter,
} from '../../ui/dialog';

const AUDIENCE_TYPES = [
  { value: 'all', label: 'All Fans', description: 'Every registered user and subscriber' },
  { value: 'premium', label: 'Premium Subscribers', description: 'Active premium members only' },
  { value: 'purchasers', label: 'Previous Buyers', description: 'Users who have made purchases' },
  { value: 'engaged', label: 'Engaged Fans', description: 'Users who liked, commented, or pre-saved' },
  { value: 'inactive', label: 'Inactive Users', description: 'Registered but never purchased' },
  { value: 'subscribers', label: 'Newsletter Subscribers', description: 'Opted-in for new release alerts' },
];

export function CampaignForm({ 
  formData, 
  setFormData, 
  audiencePreview, 
  onAudienceChange,
  testEmail,
  setTestEmail,
  onSendTest,
  onSubmit,
  onCancel,
  isEditing 
}) {
  return (
    <>
      <div className="space-y-4 py-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Campaign Name</label>
          <Input
            placeholder="e.g., New Album Announcement"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="bg-white/5 border-white/10"
            data-testid="campaign-name-input"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Email Subject</label>
          <Input
            placeholder="e.g., New Music Just Dropped!"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            className="bg-white/5 border-white/10"
            data-testid="campaign-subject-input"
          />
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Target Audience</label>
          <Select value={formData.audience_type} onValueChange={onAudienceChange}>
            <SelectTrigger className="bg-white/5 border-white/10" data-testid="audience-select">
              <SelectValue placeholder="Select audience" />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-700">
              {AUDIENCE_TYPES.map((audience) => (
                <SelectItem key={audience.value} value={audience.value}>
                  <div>
                    <span className="font-medium">{audience.label}</span>
                    <span className="text-gray-400 text-xs ml-2">- {audience.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {audiencePreview && (
            <p className="text-sm text-cyan-400 mt-2">
              This will reach approximately {audiencePreview.recipient_count} recipients
            </p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Email Content (HTML supported)</label>
          <Textarea
            placeholder="Write your message here... You can use HTML for formatting."
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            className="bg-white/5 border-white/10 min-h-[200px]"
            data-testid="campaign-content-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Tip: Use &lt;strong&gt;bold&lt;/strong&gt;, &lt;em&gt;italic&lt;/em&gt;, &lt;a href="..."&gt;links&lt;/a&gt;
          </p>
        </div>

        {/* Test Email Section */}
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <label className="text-sm font-medium mb-2 block flex items-center gap-2">
            <TestTube className="w-4 h-4 text-yellow-400" />
            Send Test Email
          </label>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="your@email.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="bg-white/5 border-white/10 flex-1"
              data-testid="test-email-input"
            />
            <Button variant="outline" onClick={onSendTest} data-testid="send-test-btn">
              Send Test
            </Button>
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSubmit} className="bg-cyan-500 hover:bg-cyan-600" data-testid="save-campaign-btn">
          {isEditing ? 'Update Campaign' : 'Create Campaign'}
        </Button>
      </DialogFooter>
    </>
  );
}

export { AUDIENCE_TYPES };
